import { deleteObject, getBlob, listAll, ref, uploadBytes } from 'firebase/storage';

import {
  LEVEL_DESIGN_IMAGE_ASSET_COLLECTION,
  SCENE_IMAGE_ASSET_COLLECTION,
  readImageAssetDocument,
} from './imageAssetDocuments';
import { getFirebaseStorage } from './client';
import { inferImageContentType, isStorageObjectPath } from './mediaStoragePaths';
import {
  parseLevelDesignImageAssetPath,
  persistLevelDesignImageAsset,
  readLevelDesignImageAsset,
} from '../levelDesignImageAssets';
import {
  parseSceneImageAssetPath,
  persistSceneImageAsset,
  readSceneImageAsset,
} from '../sceneImageAssets';

const resolvedObjectUrlByPath = new Map<string, string>();
const pendingResolvedObjectUrlByPath = new Map<string, Promise<string>>();

const toStorageRef = (storagePath: string) => ref(getFirebaseStorage(), storagePath.trim());

export const normalizeBrowserMediaPath = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed || /^(?:[a-z]+:|\/)/i.test(trimmed)) {
    return trimmed;
  }
  return `/${trimmed}`;
};

const revokeObjectUrl = (value: string): void => {
  if (typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(value);
  }
};

const cacheResolvedObjectUrl = (storagePath: string, blob: Blob): string => {
  const trimmedStoragePath = storagePath.trim();
  const previousUrl = resolvedObjectUrlByPath.get(trimmedStoragePath);
  if (previousUrl) {
    revokeObjectUrl(previousUrl);
  }

  const objectUrl = URL.createObjectURL(blob);
  resolvedObjectUrlByPath.set(trimmedStoragePath, objectUrl);
  pendingResolvedObjectUrlByPath.set(trimmedStoragePath, Promise.resolve(objectUrl));
  return objectUrl;
};

const resolveLegacyLevelDesignAssetPathAsync = async (value: string): Promise<string | null> => {
  const assetId = parseLevelDesignImageAssetPath(value);
  if (!assetId) {
    return null;
  }

  const cached = readLevelDesignImageAsset(assetId);
  if (cached) {
    return cached;
  }

  const persisted = await readImageAssetDocument(LEVEL_DESIGN_IMAGE_ASSET_COLLECTION, assetId);
  if (!persisted?.trim()) {
    return null;
  }

  persistLevelDesignImageAsset(assetId, persisted);
  return persisted;
};

const resolveLegacySceneAssetPathAsync = async (value: string): Promise<string | null> => {
  const assetId = parseSceneImageAssetPath(value);
  if (!assetId) {
    return null;
  }

  const cached = readSceneImageAsset(assetId);
  if (cached) {
    return cached;
  }

  const persisted = await readImageAssetDocument(SCENE_IMAGE_ASSET_COLLECTION, assetId);
  if (!persisted?.trim()) {
    return null;
  }

  persistSceneImageAsset(assetId, persisted);
  return persisted;
};

const resolveLegacyMediaPathAsync = async (value: string): Promise<string | null> => {
  const levelAsset = await resolveLegacyLevelDesignAssetPathAsync(value);
  if (levelAsset) {
    return levelAsset;
  }

  return resolveLegacySceneAssetPathAsync(value);
};

const fetchBlobFromUrl = async (value: string): Promise<Blob> => {
  const response = await fetch(value);
  if (!response.ok) {
    throw new Error(`Failed to load media asset (${response.status} ${response.statusText}).`);
  }
  return response.blob();
};

const deleteStorageRefRecursively = async (storageRef: ReturnType<typeof ref>): Promise<void> => {
  const { items, prefixes } = await listAll(storageRef);
  await Promise.all(prefixes.map((prefix) => deleteStorageRefRecursively(prefix)));
  await Promise.all(items.map((item) => deleteObject(item).catch(() => undefined)));
};

export const revokeResolvedStorageObjectPath = (storagePath: string): void => {
  const trimmedStoragePath = storagePath.trim();
  const cachedUrl = resolvedObjectUrlByPath.get(trimmedStoragePath);
  if (cachedUrl) {
    revokeObjectUrl(cachedUrl);
    resolvedObjectUrlByPath.delete(trimmedStoragePath);
  }
  pendingResolvedObjectUrlByPath.delete(trimmedStoragePath);
};

export const resolveMediaPathForBrowser = async (value: string): Promise<string> => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (isStorageObjectPath(trimmed)) {
    const existing = pendingResolvedObjectUrlByPath.get(trimmed);
    if (existing) {
      return existing;
    }

    const request = getBlob(toStorageRef(trimmed))
      .then((blob) => cacheResolvedObjectUrl(trimmed, blob))
      .catch((error: unknown) => {
        pendingResolvedObjectUrlByPath.delete(trimmed);
        throw error;
      });
    pendingResolvedObjectUrlByPath.set(trimmed, request);
    return request;
  }

  const legacyResolved = await resolveLegacyMediaPathAsync(trimmed);
  if (!legacyResolved?.trim() || legacyResolved.trim() === trimmed) {
    return normalizeBrowserMediaPath(trimmed);
  }

  return resolveMediaPathForBrowser(legacyResolved.trim());
};

export const readMediaBlob = async (value: string): Promise<Blob> => {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error('Cannot read a blank media path.');
  }

  if (isStorageObjectPath(trimmed)) {
    return getBlob(toStorageRef(trimmed));
  }

  const legacyResolved = await resolveLegacyMediaPathAsync(trimmed);
  if (legacyResolved?.trim() && legacyResolved.trim() !== trimmed) {
    return readMediaBlob(legacyResolved.trim());
  }

  return fetchBlobFromUrl(trimmed);
};

export const uploadFileToStorageObjectPath = async (
  storagePath: string,
  file: File,
  metadata: {
    cacheControl?: string;
    contentType?: string;
  } = {},
): Promise<string> => {
  const trimmedStoragePath = storagePath.trim();
  await uploadBytes(toStorageRef(trimmedStoragePath), file, {
    cacheControl: metadata.cacheControl,
    contentType: metadata.contentType ?? (file.type || undefined),
  });
  cacheResolvedObjectUrl(trimmedStoragePath, file);
  return trimmedStoragePath;
};

export const uploadBlobToStorageObjectPath = async (
  storagePath: string,
  blob: Blob,
  metadata: {
    cacheControl?: string;
    contentType?: string;
  } = {},
): Promise<string> => {
  const trimmedStoragePath = storagePath.trim();
  await uploadBytes(toStorageRef(trimmedStoragePath), blob, {
    cacheControl: metadata.cacheControl,
    contentType: metadata.contentType ?? (blob.type || undefined),
  });
  cacheResolvedObjectUrl(trimmedStoragePath, blob);
  return trimmedStoragePath;
};

export const copyMediaAssetToStorageObjectPath = async (
  sourcePath: string,
  targetPath: string,
  metadata: {
    cacheControl?: string;
    contentType?: string;
  } = {},
): Promise<string> => {
  const sourceBlob = await readMediaBlob(sourcePath);
  return uploadBlobToStorageObjectPath(targetPath, sourceBlob, {
    cacheControl: metadata.cacheControl,
    contentType:
      metadata.contentType ??
      (sourceBlob.type || undefined) ??
      inferImageContentType(sourcePath) ??
      undefined,
  });
};

export const deleteStorageObjectPath = async (value: string): Promise<void> => {
  const trimmed = value.trim();
  if (!isStorageObjectPath(trimmed)) {
    return;
  }

  revokeResolvedStorageObjectPath(trimmed);
  await deleteObject(toStorageRef(trimmed)).catch(() => undefined);
};

export const deleteStoragePrefix = async (value: string): Promise<void> => {
  const trimmed = value.trim();
  if (!trimmed) {
    return;
  }

  for (const storagePath of [...resolvedObjectUrlByPath.keys()]) {
    if (storagePath.startsWith(trimmed)) {
      revokeResolvedStorageObjectPath(storagePath);
    }
  }

  await deleteStorageRefRecursively(toStorageRef(trimmed)).catch(() => undefined);
};
